import { useState } from "react";
import { supabase } from "@/lib/supabase";

/** Bucket Storage des certificats PEB (migration 059, public).
 *  Pattern aligne sur useBienPhotoUpload (biens-photos). */
const BUCKET = "peb-certificats";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `p-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function extOf(file: File): string {
  const fromName = file.name.split(".").pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{2,5}$/.test(fromName)) return fromName;
  return file.type === "application/pdf" ? "pdf" : "bin";
}

export interface PebCertificatFile {
  /** chemin storage `<bienId>/<uuid>.<ext>` — sert au DELETE */
  path: string;
  /** URL publique servie par Supabase Storage */
  url: string;
}

/** Hook upload PDF certificat PEB par le prestataire PEB.
 *  Un seul PDF par bien (V1) : `upload(file)` remplace l ancien (delete +
 *  re-insert) puis renvoie la nouvelle URL. La colonne `pebCertificatPdf`
 *  de `biens` stocke uniquement l URL. */
export function useBienPebPdfUpload(bienId: string) {
  const [uploading, setUploading] = useState(false);

  async function upload(file: File, previousPath?: string | null): Promise<PebCertificatFile> {
    setUploading(true);
    try {
      // Le nouveau fichier d'abord : si l'upload échoue, l'ancien reste
      // intact. L'ancien path est distinct (uuid frais) donc jamais de
      // collision — la suppression de l'ancien ne peut se faire qu'une fois
      // le nouveau confirmé en place (best-effort, on n'échoue pas si la
      // delete loupe : le nouveau fichier prime).
      const id = uuid();
      const path = `${bienId}/${id}.${extOf(file)}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
        contentType: file.type || "application/pdf",
        cacheControl: "3600",
        upsert: false,
      });
      if (error) throw new Error(`upload(${file.name}): ${error.message}`);
      if (previousPath) {
        try {
          await supabase.storage.from(BUCKET).remove([previousPath]);
        } catch {/* ignore */}
      }
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
      return { path, url: data.publicUrl };
    } finally {
      setUploading(false);
    }
  }

  async function remove(path: string): Promise<void> {
    const { error } = await supabase.storage.from(BUCKET).remove([path]);
    if (error) throw new Error(`remove(${path}): ${error.message}`);
  }

  /** Extrait le path storage depuis une URL publique Supabase :
   *  https://<proj>.supabase.co/storage/v1/object/public/peb-certificats/<path>
   *  Retourne null si URL non parsable (pour eviter de casser remove). */
  function pathFromUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    const marker = `/object/public/${BUCKET}/`;
    const i = url.indexOf(marker);
    if (i < 0) return null;
    return url.slice(i + marker.length);
  }

  return { upload, remove, pathFromUrl, uploading };
}
